import React, { useMemo, useState } from 'react'
import { Search } from 'Assets/icons/Search'
import { useTranslation } from 'react-i18next'
import {
  Card,
  InputWithIcon,
  Tabs,
  TabType,
  DateInput,
  DateRange,
} from '@clickcease/cc-client-components-lib'
import { DomainsFilterStyled } from './DomainsTable.styled'
import { ASC, DESC, DirectionType } from 'Types/tables'
import { DOMAIN_TAB_NAME } from 'Features/home/routes/Home/types'
import { ButtonWrapper } from './OverallDomainPreformance.styled'
import useOverallDomainPerformanceStore, {
  ACTIVE_DOMAIN_SORT_KEYS,
  DomainSortKeys,
} from 'Features/home/store/overallDomainPerformance'
import { NewDomainsTable } from './NewActiveDomainsTable'
import { NewPendingDomainsTable } from './NewPendingDomainTable'
import DateFormats from 'Assets/lists/dateFormats'
import { useGetUserProfile } from 'Features/userProfile/api/getUserProfile'
import { usePagination } from 'Hooks/usePagination'

type OverallProps = {
  innerRef: React.RefObject<HTMLDivElement>
}

export const NewOverallDomainsPerformance = (props: OverallProps) => {
  const { t } = useTranslation('dashboard')
  const [currTab, setCurrTab] = useState<number>(1)
  const {
    selectedDates,
    setSelectedDates,
    setSortDirection,
    setSortDomainsBy,
    sortDirection,
    sortDomainsBy,
  } = useOverallDomainPerformanceStore()
  const userSettings = useGetUserProfile()
  const { onSearchInputChange, debouncedSearch, search } = usePagination({})

  const locale = useMemo(() => {
    if (!(userSettings.data instanceof Error)) {
      return userSettings?.data?.locale as string
    } else {
      return DateFormats[0].value
    }
  }, [userSettings.data])

  const handleSort = (columnName: DomainSortKeys) => {
    let sortOrder: DirectionType = DESC
    if (sortDomainsBy !== columnName) {
      setSortDomainsBy(columnName)
    }
    if (sortDomainsBy === columnName) {
      sortOrder = sortDirection === ASC ? DESC : ASC
    }
    setSortDirection(sortOrder)
  }

  const onChangeTab = (tab: TabType) => {
    handleSort(ACTIVE_DOMAIN_SORT_KEYS.ADDRESS)
    setCurrTab(tab.id)
  }

  const tabs: Record<(typeof DOMAIN_TAB_NAME)[keyof typeof DOMAIN_TAB_NAME], TabType> = {
    [DOMAIN_TAB_NAME.active]: {
      id: 1,
      title: t('overall_domain_performance.active'),
      page: <NewDomainsTable debouncedSearch={debouncedSearch} />,
    },
    [DOMAIN_TAB_NAME.pending]: {
      id: 2,
      title: t('overall_domain_performance.pending'),
      page: <NewPendingDomainsTable debouncedSearch={debouncedSearch} />,
    },
  }

  const setDateRange = (dates: DateRange | undefined) => {
    if (dates) {
      setSelectedDates(dates)
    }
  }

  const findTabById = (id: number) => {
    return Object.values(tabs).find((tab) => tab.id === id) || tabs[DOMAIN_TAB_NAME.pending]
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentTab = useMemo(() => findTabById(currTab), [currTab])

  return (
    <Card
      id={'home-overall-domains-performance'}
      title={t('overall_domain_performance.title')}
      headerUtilities={
        <ButtonWrapper ref={props.innerRef}>{/* <DomainsCSVExport domains={} /> */}</ButtonWrapper>
      }
    >
      <DomainsFilterStyled>
        <DateInput
          onSelect={setDateRange}
          selectedDateRange={selectedDates}
          localeFormat={locale}
        />
        <InputWithIcon
          css={{ width: '250px', height: '32px' }}
          value={search}
          placeholder={t('overall_domain_performance.search')}
          onChange={onSearchInputChange}
          iconLeft={<Search />}
        />
      </DomainsFilterStyled>

      <Tabs
        tabs={Object.values(tabs)}
        activeTab={currentTab}
        onClick={onChangeTab}
        externalControl
      />
    </Card>
  )
}
